<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { formatFullName } from "@/helpers/name";

import type { ProfilPreviewModalProps } from "@/components/modal/Profile/previewProfile/types";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import Modal from "@/dsComponents/modals/Modal.vue";
import Separator from "@/dsComponents/Separator.vue";

import { useProfilPreview } from "@/components/modal/Profile/previewProfile/composables/useProfilPreview";
import ProfilPreviewAlert from "@/components/modal/Profile/previewProfile/ProfilPreviewAlert.vue";
import ProfilPreviewFollowingTab from "@/components/modal/Profile/previewProfile/ProfilPreviewFollowingTab.vue";
import ProfilPreviewForm from "@/components/modal/Profile/previewProfile/ProfilPreviewForm.vue";
import ProfilPreviewModalContent from "@/components/modal/Profile/previewProfile/ProfilPreviewModalContent.vue";
import ProfilPreviewModalHeader from "@/components/modal/Profile/previewProfile/ProfilPreviewModalHeader.vue";
import ProfilPreviewQualifications from "@/components/modal/Profile/previewProfile/ProfilPreviewQualifications.vue";
import ProfilPreviewWarningModal from "@/components/modal/Profile/previewProfile/ProfilPreviewWarningModal.vue";

const props = withDefaults(defineProps<ProfilPreviewModalProps>(), {
    open: false,
});

const emit = defineEmits<{
    (e: "close"): void;
    (e: "openProfil"): void;
}>();

const { t } = useI18n();

const profilePreviewModalActions = {
    NEXT_PROFILE: "next-profile",
    PREVIOUS_PROFILE: "previous-profile",
    OPEN_PROFIL: "open-profil",
    CLOSE_MAIN_MODAL: "close-main-modal",
};

const isWarningOpen = ref<boolean>(false);
const currentAction = ref<string | null>(null);
const headerRef = ref<InstanceType<typeof ProfilPreviewModalHeader> | null>(null);

const {
    totalProfiles,
    currentProfileIndex,
    currentProfile,
    updatedProfile,
    formattedUpdatedProfilAddress,
    hasChanges,
    handlePreviousProfile,
    handleNextProfile,
    handleOpenProfil,
    updateProfileField,
    updateProfileAddress,
    handleAddJob,
    handleUpdateExperience,
    removeJob,
    saveProfile,
} = useProfilPreview(props.profilIds, props.startingProfileId);

const profileName = computed(() => {
    return formatFullName(updatedProfile.value);
});

const handleCloseWarningModal = () => {
    isWarningOpen.value = false;
};

const isTabActive = (tabId: string) => {
    return headerRef.value?.isTabActive?.(tabId) ?? false;
};

const handleAction = () => {
    switch (currentAction.value) {
        case profilePreviewModalActions.NEXT_PROFILE:
            handleNextProfile();
            break;
        case profilePreviewModalActions.PREVIOUS_PROFILE:
            handlePreviousProfile();
            break;
        case profilePreviewModalActions.OPEN_PROFIL:
            emit("openProfil");
            handleOpenProfil();
            break;
        case profilePreviewModalActions.CLOSE_MAIN_MODAL:
            emit("close");
            break;
        default:
            return;
    }
    currentAction.value = null;
};

const handleSaveChanges = async () => {
    await saveProfile();
    handleAction();
    handleCloseWarningModal();
};

const handleContinueWithoutSaving = () => {
    handleAction();
    handleCloseWarningModal();
};

const dispatchAction = (action: string) => {
    currentAction.value = action;
    if (hasChanges.value) {
        isWarningOpen.value = true;
    } else {
        handleAction();
    }
};
</script>

<template>
    <Modal :open="open" width="1200px" height="100%" position="top" noPadding @close="dispatchAction(profilePreviewModalActions.CLOSE_MAIN_MODAL)">
        <div class="profil-preview-modal">
            <ProfilPreviewModalHeader
                ref="headerRef"
                :currentProfileIndex="currentProfileIndex"
                :currentProfile="currentProfile"
                :totalProfiles="totalProfiles"
                :profileName="profileName"
                :actions="actions"
                @previous="dispatchAction(profilePreviewModalActions.PREVIOUS_PROFILE)"
                @next="dispatchAction(profilePreviewModalActions.NEXT_PROFILE)"
                @openProfile="dispatchAction(profilePreviewModalActions.OPEN_PROFIL)"
            />

            <div class="profil-preview-modal__container">
                <ProfilPreviewModalContent :cvPath="currentProfile?.cv?.path ?? null" />

                <div class="profil-preview-modal__aside">
                    <div
                        class="profil-preview-modal__aside__container"
                        :class="{ 'profil-preview-modal__aside__container--following': isTabActive('following') }"
                    >
                        <div class="profil-preview-modal__aside__content">
                            <div v-if="isTabActive('profil')" class="profil-preview-modal__aside__section">
                                <ProfilPreviewAlert :recruitmentCount="updatedProfile?.recruitmentCount ?? 0" />
                            </div>
                            <template v-if="isTabActive('profil') && updatedProfile">
                                <div class="profil-preview-modal__aside__section">
                                    <ProfilPreviewForm
                                        :profile="updatedProfile"
                                        :address="formattedUpdatedProfilAddress"
                                        @update:field="updateProfileField"
                                        @update:address="updateProfileAddress"
                                    />
                                </div>
                                <Separator />
                                <div class="profil-preview-modal__aside__section">
                                    <ProfilPreviewQualifications
                                        :profile="updatedProfile"
                                        @update:experience="handleUpdateExperience"
                                        @addJob="handleAddJob"
                                        @removeJob="removeJob"
                                    />
                                </div>
                            </template>
                            <template v-else-if="isTabActive('following')">
                                <div class="profil-preview-modal__aside__section profil-preview-modal__aside__section--following">
                                    <ProfilPreviewFollowingTab :worker-id="currentProfile?.id" :agencies="updatedProfile?.agencies ?? []" />
                                </div>
                            </template>
                        </div>
                    </div>
                    <div v-if="hasChanges" class="profil-preview-modal__aside__footer">
                        <ButtonAlt
                            :label="t('modals.profilPreview.actions.save')"
                            variant="filled"
                            fullWidth
                            data-bot="save-changes"
                            @click="handleSaveChanges"
                        />
                    </div>
                </div>
            </div>

            <ProfilPreviewWarningModal
                :open="isWarningOpen"
                @close="handleCloseWarningModal"
                @continue-without-saving="handleContinueWithoutSaving"
                @save="handleSaveChanges"
            />
        </div>
    </Modal>
</template>

<style scoped lang="scss">
.profil-preview-modal {
    display: flex;
    flex-direction: column;
    height: 100%;

    &__container {
        display: flex;
        flex: 1 0 0;
    }

    &__aside {
        display: flex;
        flex-direction: column;
        width: 420px;
        background-color: var(--bg-white-0, #ffffff);
        border-left: 1px solid var(--stroke-soft-200, #dbdfe6);

        &__container {
            display: flex;
            flex-direction: column;
            align-items: center;
            flex: 1 0 0;
            align-self: stretch;
            gap: var(--spacing-xl, 16px);
            overflow: auto;
            padding: var(--spacing-3xl, 24px) var(--spacing-lg, 12px) var(--spacing-3xl, 24px) 0;

            &--following {
                padding: 0;
            }
        }

        &__content {
            height: 100%;
            display: flex;
            flex-direction: column;
            gap: var(--spacing-3xl, 24px);
            align-self: stretch;
        }

        &__section {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-xl, 16px);
            padding: 0 var(--spacing-lg, 12px);
            align-self: stretch;

            &--following {
                flex: 1;
                min-height: 0;
                padding: 0;
            }
        }

        &__footer {
            padding: var(--spacing-xl, 16px) var(--spacing-3xl, 24px);
            border-top: 1px solid var(--stroke-soft-200, #dbdfe6);
        }
    }
}
</style>

<style lang="scss">
.modal-card {
    &:has(.profil-preview-modal) {
        overflow: hidden;
    }
}
</style>
